/**
 * Swept-segment spans.
 *
 * A stroke is a chain of samples s[0..n]. Segment i covers capsule(s[i-1], s[i]). Because the capsule
 * contains sphere(s[i-1]) and that sphere was already written by segment i-1 (or by the full-sphere
 * stamp at a break), segment i only needs to write the *crescent*: cells inside capsule(prev, cur) but
 * outside sphere(prev). Per (y, z) row that is the capsule span minus the sphere(prev) span, which is
 * at most two sub-spans because both are intervals and the sphere span is contained in the capsule span.
 *
 * Writing the crescent instead of the whole capsule visits far fewer cells and leaves the fold result
 * identical to stamping every capsule fully (see rasterize.ts for the split invariant).
 *
 * Lumpy (banded) variants (materials-perf-plan B4): with `lumpFx > 0` every cell has its own effective
 * radius in [r - lump, r] (noise.ts), so a primitive's row is no longer one interval. The banded sweep
 * keeps the exact `rowSpan` for the *inner* primitive (radius r - lump: those cells are in for every
 * noise value) and the *outer* one (radius r: everything beyond is out), and runs the per-cell noise
 * test only on the shell between them. The inclusion rule is monotone in distance, and
 * dist(cell, capsule(prev, cur)) <= dist(cell, prev), so a cell in lumpy sphere(prev) is in lumpy
 * capsule(prev, cur): the crescent is still exactly capsule \ sphere(prev) and the split invariant holds.
 */

import { FX_ONE, fx3Equal, type Fx3 } from './fixed'
import { capsule, insideAtRadiusFx, roundConeContainsFx, rowRange, rowSpan, sphere, type RowRange } from './primitives'
import { clampLumpFx, LumpRowNoise, lumpRadiusFx } from './noise'

export type SpanEmit = (y: number, z: number, x0: number, x1: number) => void

/**
 * Optional inclusive cell box the caller will clip spans to anyway (the document bounds): rows outside
 * it are skipped before any per-cell work, and the tapered sweep scans x only inside it. Every span
 * emitted inside the box is unchanged, so clipped results equal unclipped results after clipping.
 * Matters for big spheres in shallow documents (a sunk "lens" bead is a sphere of radius ~ w^2 / 2h).
 */
export type CellClip = { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }

function clipRows(rows: RowRange, clip: CellClip | undefined): RowRange {
  if (!clip) return rows
  return { y0: Math.max(rows.y0, clip.y0), y1: Math.min(rows.y1, clip.y1), z0: Math.max(rows.z0, clip.z0), z1: Math.min(rows.z1, clip.z1) }
}

/**
 * Emit the spans of one segment. With `prev === null` (or prev === cur) the full sphere at `cur` is
 * emitted; otherwise the crescent capsule(prev, cur) \ sphere(prev). Spans are in cell coordinates and
 * are not clipped to any bounds.
 */
export function forEachSegmentSpans(prev: Fx3 | null, cur: Fx3, rFx: number, emit: SpanEmit, clip?: CellClip): void {
  if (prev === null) {
    forEachSphereSpans(cur, rFx, emit, clip)
    return
  }
  if (fx3Equal(prev, cur)) return // zero-length segment adds nothing beyond sphere(prev)
  const cap = capsule(prev, cur, rFx)
  const sph = sphere(prev, rFx)
  const rows = clipRows(rowRange(cap), clip)
  for (let z = rows.z0; z <= rows.z1; z++) {
    for (let y = rows.y0; y <= rows.y1; y++) {
      const c = rowSpan(cap, y, z)
      if (!c) continue
      const s = rowSpan(sph, y, z)
      if (!s) { emit(y, z, c[0], c[1]); continue }
      // sphere(prev) ⊂ capsule, so s ⊂ c: left and right remainders
      if (c[0] <= s[0] - 1) emit(y, z, c[0], s[0] - 1)
      if (s[1] + 1 <= c[1]) emit(y, z, s[1] + 1, c[1])
    }
  }
}

/**
 * Tapered segment (per-sample radii, `BrushStrokePayload.radii`): the crescent of the round cone from
 * sphere(prev, rPrev) to sphere(cur, rCur), minus sphere(prev, rPrev). The round cone always holds both
 * of its end spheres exactly (`roundConeContainsFx`), so sphere(prev, rPrev) was written whole by the
 * previous segment and the crescent decomposition and the split invariant hold as for `forEachSegmentSpans`.
 * Equal radii take the exact capsule path. `prev === null` stamps sphere(cur, rCur). Spans are emitted
 * as maximal runs in increasing x within a row and never overlap; they are not clipped.
 */
export function forEachSegmentSpansTapered(prev: Fx3 | null, rPrev: number, cur: Fx3, rCur: number, emit: SpanEmit, clip?: CellClip): void {
  if (prev === null) {
    forEachSphereSpans(cur, rCur, emit, clip)
    return
  }
  if (rPrev === rCur) {
    forEachSegmentSpans(prev, cur, rCur, emit, clip)
    return
  }
  if (fx3Equal(prev, cur) && rCur < rPrev) return // the new sphere sits inside sphere(prev)
  const rMax = Math.max(rPrev, rCur)
  const prevSphere = sphere(prev, rPrev)
  const rows = clipRows(rowRange(capsule(prev, cur, rMax)), clip)
  let xLo = Math.ceil((Math.min(prev[0], cur[0]) - rMax) / FX_ONE)
  let xHi = Math.floor((Math.max(prev[0], cur[0]) + rMax) / FX_ONE)
  if (clip) { xLo = Math.max(xLo, clip.x0); xHi = Math.min(xHi, clip.x1) }
  for (let z = rows.z0; z <= rows.z1; z++) {
    const Z = z * FX_ONE
    for (let y = rows.y0; y <= rows.y1; y++) {
      const Y = y * FX_ONE
      const s = rowSpan(prevSphere, y, z)
      let run = Number.NaN
      for (let x = xLo; x <= xHi; x++) {
        if (s && x >= s[0] && x <= s[1]) {
          if (!Number.isNaN(run)) { emit(y, z, run, x - 1); run = Number.NaN }
          x = s[1]
          continue
        }
        if (roundConeContainsFx(prev, rPrev, cur, rCur, x * FX_ONE, Y, Z)) {
          if (Number.isNaN(run)) run = x
        } else if (!Number.isNaN(run)) { emit(y, z, run, x - 1); run = Number.NaN }
      }
      if (!Number.isNaN(run)) emit(y, z, run, xHi)
    }
  }
}

/** Emit every row span of a full sphere (unclipped). */
export function forEachSphereSpans(c: Fx3, rFx: number, emit: SpanEmit, clip?: CellClip): void {
  const p = sphere(c, rFx)
  const rows = clipRows(rowRange(p), clip)
  for (let z = rows.z0; z <= rows.z1; z++) {
    for (let y = rows.y0; y <= rows.y1; y++) {
      const s = rowSpan(p, y, z)
      if (s) emit(y, z, s[0], s[1])
    }
  }
}

/** Emit every row span of a full capsule (unclipped). Used by tests and the hover path. */
export function forEachCapsuleSpans(a: Fx3, b: Fx3, rFx: number, emit: SpanEmit): void {
  const p = capsule(a, b, rFx)
  const rows = rowRange(p)
  for (let z = rows.z0; z <= rows.z1; z++) {
    for (let y = rows.y0; y <= rows.y1; y++) {
      const s = rowSpan(p, y, z)
      if (s) emit(y, z, s[0], s[1])
    }
  }
}

/** Cell-space bounding box of a sphere at `c` (inclusive, unclipped). */
export function sphereCellBox(c: Fx3, rFx: number): { min: [number, number, number]; max: [number, number, number] } {
  return {
    min: [Math.ceil((c[0] - rFx) / FX_ONE), Math.ceil((c[1] - rFx) / FX_ONE), Math.ceil((c[2] - rFx) / FX_ONE)],
    max: [Math.floor((c[0] + rFx) / FX_ONE), Math.floor((c[1] + rFx) / FX_ONE), Math.floor((c[2] + rFx) / FX_ONE)],
  }
}

// ----------------------------------------------------------------------------
// Lumpy (banded) sweep
// ----------------------------------------------------------------------------

/** Per-stroke lump parameters, already clamped (`lumpFx <= rFx`, `cell >= 1`). */
export type LumpParams = { readonly lumpFx: number; readonly cell: number; readonly seed: number }

/** Normalise raw snapshot lump fields; null means "no lump" (plain sweep). */
export function resolveLumpParams(rFx: number, lumpFx: number | undefined, cell: number | undefined, seed: number | undefined): LumpParams | null {
  if (!lumpFx || seed == null) return null
  const l = clampLumpFx(lumpFx, rFx)
  if (l <= 0) return null
  return { lumpFx: l, cell: Math.max(1, (cell ?? 1) | 0), seed: seed | 0 }
}

/**
 * Per-stroke lookup: rEff(f)^2 for every lump field value f, so the shell test is a table read and
 * one compare instead of a division per cell. Values stay below 2^53 (rFx <= 2^20).
 */
function lumpRadius2Table(rFx: number, lumpFx: number): Float64Array {
  const t = new Float64Array(256)
  for (let f = 0; f < 256; f++) { const r = lumpRadiusFx(rFx, lumpFx, f); t[f] = r < 0 ? -1 : r * r }
  return t
}

// Allocation-free exact sphere row span (same cells as `rowSpan(sphere(...))`): the float sqrt gives
// the estimate, the integer test fixes it up. Result in SPAN_X0 / SPAN_X1; returns false when empty.
let SPAN_X0 = 0
let SPAN_X1 = 0
function sphereRowSpanFast(cx: number, d2: number): boolean {
  if (d2 < 0) return false
  const w = Math.sqrt(d2)
  let x0 = Math.ceil((cx - w) / FX_ONE)
  let x1 = Math.floor((cx + w) / FX_ONE)
  let t = (x0 - 1) * FX_ONE - cx
  while (t * t <= d2) { x0--; t = (x0 - 1) * FX_ONE - cx }
  t = x0 * FX_ONE - cx
  while (x0 <= x1 && t * t > d2) { x0++; t = x0 * FX_ONE - cx }
  t = (x1 + 1) * FX_ONE - cx
  while (t * t <= d2) { x1++; t = (x1 + 1) * FX_ONE - cx }
  t = x1 * FX_ONE - cx
  while (x1 >= x0 && t * t > d2) { x1--; t = x1 * FX_ONE - cx }
  if (x0 > x1) return false
  SPAN_X0 = x0; SPAN_X1 = x1
  return true
}

/**
 * Emit the spans of one segment with a lumpy shell. Same contract as `forEachSegmentSpans`:
 * `prev === null` stamps the lumpy sphere at `cur`, otherwise the lumpy crescent. Spans are emitted
 * as maximal runs of included cells, in increasing x within a row, and never overlap.
 *
 * Per row the walk covers a cheap superset of the outer capsule span (half-width from the row's 2D
 * distance to the segment) minus the exact inner sphere(prev) span, and each cell is decided with the
 * exact integer predicates (`capsuleContainsFx`'s arithmetic, inlined). A cell whose projection on
 * the segment falls at or behind `prev` (dot <= 0) has the same distance to the capsule as to `prev`,
 * so it is in the lumpy capsule iff it is in the lumpy sphere(prev): never a crescent cell, skipped
 * without noise. Noise is read only for cells in one of the two shells ahead of `prev`.
 */
export function forEachSegmentSpansBanded(prev: Fx3 | null, cur: Fx3, rFx: number, lump: LumpParams, emit: SpanEmit, clip?: CellClip): void {
  if (prev === null) {
    forEachSphereSpansBanded(cur, rFx, lump, emit, clip)
    return
  }
  if (fx3Equal(prev, cur)) return
  const rIn = rFx - lump.lumpFx
  const rr = rFx * rFx, rrIn = rIn * rIn
  const r2 = lumpRadius2Table(rFx, lump.lumpFx)
  const noise = new LumpRowNoise(lump.cell, lump.seed)
  const ax = prev[0], ay = prev[1], az = prev[2]
  const bx = cur[0], by = cur[1], bz = cur[2]
  const dx = bx - ax, dy = by - ay, dz = bz - az
  const L2 = dx * dx + dy * dy + dz * dz
  const rrL2 = rr * L2, rrInL2 = rrIn * L2
  const Lyz2 = dy * dy + dz * dz
  const xMin = Math.min(ax, bx), xMax = Math.max(ax, bx)
  const rows = clipRows(rowRange(capsule(prev, cur, rFx)), clip)
  for (let z = rows.z0; z <= rows.z1; z++) {
    const Z = z * FX_ONE
    const dza = Z - az, dzb = Z - bz
    for (let y = rows.y0; y <= rows.y1; y++) {
      const Y = y * FX_ONE
      const dya = Y - ay, dyb = Y - by
      const Dyz = dya * dy + dza * dz
      // Cells with dot <= 0 are never crescent cells. dot(X) = (X - ax) * dx + Dyz is linear in X.
      if (dx === 0 && Dyz <= 0) continue
      const Cyz = dya * dya + dza * dza
      // Superset of the capsule's x extent on this row from the 2D distance of (Y, Z) to the segment.
      let dmin2: number
      if (Lyz2 === 0) dmin2 = Cyz
      else {
        const t = Dyz / Lyz2
        dmin2 = t <= 0 ? Cyz : t >= 1 ? dyb * dyb + dzb * dzb : Cyz - (Dyz * Dyz) / Lyz2
      }
      const w2 = rr - dmin2
      if (w2 < -FX_ONE) continue
      const w = Math.sqrt(Math.max(0, w2)) + FX_ONE
      let x0 = Math.ceil((xMin - w) / FX_ONE), x1 = Math.floor((xMax + w) / FX_ONE)
      if (dx > 0) { const t = Math.floor((ax - Dyz / dx) / FX_ONE); if (t > x0) x0 = t }
      else if (dx < 0) { const t = Math.ceil((ax - Dyz / dx) / FX_ONE); if (t < x1) x1 = t }
      if (x0 > x1) continue
      const hasIs = sphereRowSpanFast(ax, rrIn - Cyz)
      const is0 = SPAN_X0, is1 = SPAN_X1
      const Cyzb = dyb * dyb + dzb * dzb
      noise.beginRow(y, z)
      let run = Number.NaN
      for (let x = x0; x <= x1; x++) {
        // Inside the inner sphere(prev): already written by the previous segment, whatever the noise.
        if (hasIs && x >= is0 && x <= is1) {
          if (!Number.isNaN(run)) { emit(y, z, run, x - 1); run = Number.NaN }
          x = is1
          continue
        }
        const X = x * FX_ONE
        const px = X - ax
        const dot = px * dx + Dyz
        let drop: boolean
        if (dot <= 0) drop = true
        else {
          const dS2 = px * px + Cyz
          // Squared distance to the capsule core, scaled: beyond b it is the distance to b, otherwise
          // dist^2 * L2 = dS2 * L2 - dot^2 (all exact integers).
          let dC2 = 0, dC2L2 = 0
          const beyond = dot >= L2
          if (beyond) { const qx = X - bx; dC2 = qx * qx + Cyzb }
          else dC2L2 = dS2 * L2 - dot * dot
          if (beyond ? dC2 > rr : dC2L2 > rrL2) drop = true                 // outside the capsule at all
          else if ((beyond ? dC2 <= rrIn : dC2L2 <= rrInL2) && dS2 > rr) drop = false // inner capsule, outside sphere(prev)
          else {
            const rEff2 = r2[noise.at(x)]
            if (dS2 <= rEff2) drop = true                                   // lumpy sphere(prev) already holds it
            else drop = beyond ? dC2 > rEff2 : dC2L2 > rEff2 * L2
          }
        }
        if (drop) {
          if (!Number.isNaN(run)) { emit(y, z, run, x - 1); run = Number.NaN }
        } else if (Number.isNaN(run)) run = x
      }
      if (!Number.isNaN(run)) emit(y, z, run, x1)
    }
  }
}

/** Emit every row span of a lumpy sphere (unclipped), as maximal runs of included cells. */
export function forEachSphereSpansBanded(c: Fx3, rFx: number, lump: LumpParams, emit: SpanEmit, clip?: CellClip): void {
  const rIn = rFx - lump.lumpFx
  const rr = rFx * rFx, rrIn = rIn * rIn
  const r2 = lumpRadius2Table(rFx, lump.lumpFx)
  const noise = new LumpRowNoise(lump.cell, lump.seed)
  const cx = c[0]
  const rows = clipRows(rowRange(sphere(c, rFx)), clip)
  for (let z = rows.z0; z <= rows.z1; z++) {
    const dzc = z * FX_ONE - c[2]
    for (let y = rows.y0; y <= rows.y1; y++) {
      const dyc = y * FX_ONE - c[1]
      const Cyz = dyc * dyc + dzc * dzc
      if (!sphereRowSpanFast(cx, rr - Cyz)) continue
      const o0 = SPAN_X0, o1 = SPAN_X1
      const hasIn = sphereRowSpanFast(cx, rrIn - Cyz)
      const i0 = SPAN_X0, i1 = SPAN_X1
      noise.beginRow(y, z)
      const band = (x: number) => { const px = x * FX_ONE - cx; return px * px + Cyz <= r2[noise.at(x)] }
      if (!hasIn) {
        const open = emitBandRuns(band, y, z, o0, o1, Number.NaN, emit)
        if (!Number.isNaN(open)) emit(y, z, open, o1)
        continue
      }
      // Left shell, then the unconditional core, then the right shell: one chain of runs.
      const run = emitBandRuns(band, y, z, o0, i0 - 1, Number.NaN, emit)
      const core = Number.isNaN(run) ? i0 : run
      const tail = emitBandRuns(band, y, z, i1 + 1, o1, core, emit)
      if (!Number.isNaN(tail)) emit(y, z, tail, o1)
    }
  }
}

/** Emit every row span of a lumpy capsule (unclipped). Used by tests. */
export function forEachCapsuleSpansBanded(a: Fx3, b: Fx3, rFx: number, lump: LumpParams, emit: SpanEmit): void {
  const outer = capsule(a, b, rFx)
  const inner = capsule(a, b, rFx - lump.lumpFx)
  const noise = new LumpRowNoise(lump.cell, lump.seed)
  const rows = rowRange(outer)
  for (let z = rows.z0; z <= rows.z1; z++) {
    for (let y = rows.y0; y <= rows.y1; y++) {
      const o = rowSpan(outer, y, z)
      if (!o) continue
      const i = rowSpan(inner, y, z)
      noise.beginRow(y, z)
      const band = (x: number) => insideAtRadiusFx(outer, x, y, z, lumpRadiusFx(rFx, lump.lumpFx, noise.at(x)))
      if (!i) {
        const open = emitBandRuns(band, y, z, o[0], o[1], Number.NaN, emit)
        if (!Number.isNaN(open)) emit(y, z, open, o[1])
        continue
      }
      const run = emitBandRuns(band, y, z, o[0], i[0] - 1, Number.NaN, emit)
      const core = Number.isNaN(run) ? i[0] : run
      const tail = emitBandRuns(band, y, z, i[1] + 1, o[1], core, emit)
      if (!Number.isNaN(tail)) emit(y, z, tail, o[1])
    }
  }
}

/**
 * Walk the shell cells x0..x1 of one row, emitting maximal runs of cells for which `inside` holds.
 * `run` is the start of a run already open when entering (NaN for none); returns the start of the run
 * still open at x1 (NaN for none) so a caller can continue it. When x0 > x1 nothing is tested.
 */
function emitBandRuns(
  inside: (x: number) => boolean,
  y: number, z: number, x0: number, x1: number, run: number, emit: SpanEmit,
): number {
  for (let x = x0; x <= x1; x++) {
    if (inside(x)) { if (Number.isNaN(run)) run = x }
    else if (!Number.isNaN(run)) { emit(y, z, run, x - 1); run = Number.NaN }
  }
  return run
}
